import { createNamedSheetsWorkbook } from '../relatorioExcelUtils';
import { fetchDadosExcel } from '@/services/relatorioTransacoesService';
import type { ICompraDetalheItem, ITransacaoAgrupada, IVendaDetalheItem } from '@/types/relatorioTransacoes';
import type { IExcelReportResult } from './abateExcelReportStrategy';

interface IComercializacaoExcelReportStrategyDeps {
  formatarDataBR: (value: string | null | undefined) => string;
  normalizeMesKey: (value: unknown) => string;
}

export default class ComercializacaoExcelReportStrategy {
  private readonly formatarDataBR: IComercializacaoExcelReportStrategyDeps['formatarDataBR'];
  private readonly normalizeMesKey: IComercializacaoExcelReportStrategyDeps['normalizeMesKey'];

  constructor({ formatarDataBR, normalizeMesKey }: IComercializacaoExcelReportStrategyDeps) {
    this.formatarDataBR = formatarDataBR;
    this.normalizeMesKey = normalizeMesKey;
  }

  async build(item: ITransacaoAgrupada): Promise<IExcelReportResult> {
    const mes = this.normalizeMesKey(item.mes);
    const unidadeId = item.unidade;

    if (!mes || !unidadeId) {
      return { empty: true };
    }

    const [vendas, compras] = await Promise.all([
      fetchDadosExcel({ tipo: 'expedicao', mes, unidadeId }).catch(() => ({ sintetico: [], analitico: [] })),
      fetchDadosExcel({ tipo: 'recebimento', mes, unidadeId }).catch(() => ({ sintetico: [], analitico: [] })),
    ]);

    const resultVendas = vendas.sintetico as unknown as IVendaDetalheItem[];
    const resultCompras = compras.sintetico as unknown as ICompraDetalheItem[];

    if (!resultVendas.length && !resultCompras.length) {
      return { empty: true };
    }

    const vendasRows = resultVendas.map((resultado) => ({
      'Data pedido': this.formatarDataBR(resultado.data_pedido),
      'Código MAPA': resultado.cod_mapa,
      Descrição: resultado.descricao,
      Quantidade: resultado.total,
      Estado: resultado.estado,
      País: resultado.pais ?? '',
      Nacional: resultado.ambito_nacional ? 'Sim' : 'Não',
      'Nº estabelecimento': resultado.numero_estabelecimento ?? '',
    }));

    const comprasRows = resultCompras.map((resultado) => ({
      'Data compra': this.formatarDataBR(resultado.data_compra),
      'Código MAPA': resultado.cod_mapa,
      Descrição: resultado.descricao,
      Quantidade: resultado.total,
      'Nº POA': resultado.numero_poa ?? '',
      'Tipo operador': resultado.tipo_operador ?? '',
      'CPF/CNPJ': resultado.cpf_cnpj ?? '',
      UF: resultado.cod_uf ?? '',
      Município: resultado.cod_municipio ?? '',
      País: resultado.pais ?? '',
      'Estabelecimento estrangeiro': resultado.nome_estabelecimento_estrangeiro ?? '',
    }));

    const fileName = `mapa-comercializacao_${mes}.xlsx`;
    const workbook = createNamedSheetsWorkbook([
      { name: 'Vendas', rows: vendasRows },
      { name: 'Compras', rows: comprasRows },
    ]);

    return { workbook, fileName };
  }
}
